import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from './ui/button';
import { Loader2, Download, FileCheck2, Pencil, Search, Inbox, AlertTriangle } from 'lucide-react';
import { openAuthed } from '../lib/authSession';
import { OutcomeBadge } from './AdminDealComplianceTab';

const fmtSize = (b) => (b == null ? '—' : b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`);

export const StatusBadge = ({ status, needsReReview }) => {
  if (status === 'for_review') {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-orange-100 text-orange-800 border border-orange-200" data-testid="queue-status-for-review">
        <AlertTriangle className="h-3 w-3" />{needsReReview ? 'Updated — For Review' : 'For Review'}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200" data-testid="queue-status-reviewed">
      <FileCheck2 className="h-3 w-3" />Reviewed
    </span>
  );
};

/**
 * FinalPackageQueue — every Final Compliance Package built in any agent's
 * "My Clients' Folder", with its compliance review status.
 */
const FinalPackageQueue = ({ apiUrl, adminPassword, refreshKey, onStartReview, onEditReview, onCounts }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('for_review');
  const headers = useMemo(() => ({ 'X-Admin-Password': adminPassword }), [adminPassword]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${apiUrl}/api/admin/deal-compliance/queue`, { headers });
      if (r.ok) {
        const data = await r.json();
        setItems(data.items || []);
        onCounts?.(data.counts || {});
      }
    } catch (e) {
      console.error('Failed to load review queue:', e);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, headers, onCounts]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const visible = items.filter(i => {
    const q = search.toLowerCase();
    if (q && ![i.client_name, i.agent_name, i.filename].some(v => (v || '').toLowerCase().includes(q))) return false;
    return filter === 'all' || i.status === filter;
  });

  return (
    <div data-testid="final-package-queue">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input type="text" placeholder="Search client or agent…" value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0077B6]" data-testid="queue-search" />
        </div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="px-3 py-1.5 text-sm border border-gray-300 rounded-md" data-testid="queue-filter">
          <option value="for_review">For review</option>
          <option value="reviewed">Reviewed</option>
          <option value="all">All packages</option>
        </select>
      </div>

      {loading ? (
        <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" /></div>
      ) : visible.length === 0 ? (
        <div className="py-12 text-center text-sm text-gray-500 flex flex-col items-center gap-2" data-testid="queue-empty">
          <Inbox className="h-8 w-8 text-gray-300" />
          {items.length === 0 ? 'No Final Compliance Packages have been created in any agent folder yet.' : 'Nothing matches this filter.'}
        </div>
      ) : (
        <div className="overflow-x-auto border rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-600">
              <tr>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Client</th>
                <th className="text-left px-3 py-2">Agent</th>
                <th className="text-left px-3 py-2">Final Package</th>
                <th className="text-left px-3 py-2">Created</th>
                <th className="text-left px-3 py-2">Review</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {visible.map(i => (
                <tr key={i.folder_id} className={`hover:bg-gray-50 ${i.status === 'for_review' ? 'bg-orange-50/40' : ''}`} data-testid={`queue-row-${i.folder_id}`}>
                  <td className="px-3 py-2"><StatusBadge status={i.status} needsReReview={i.needs_re_review} /></td>
                  <td className="px-3 py-2 font-medium text-gray-800" data-testid={`queue-client-${i.folder_id}`}>{i.client_name}</td>
                  <td className="px-3 py-2 text-gray-700">
                    {i.agent_name || '—'}
                    {i.agent_email && <p className="text-xs text-gray-400">{i.agent_email}</p>}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-700">
                    <p className="truncate max-w-[240px]" title={i.filename}>{i.filename}</p>
                    <p className="text-gray-400">
                      {i.page_count ? `${i.page_count} pages • ` : ''}{fmtSize(i.file_size)}
                      {i.compressed && i.original_size ? ` (compressed from ${fmtSize(i.original_size)})` : ''}
                    </p>
                    {i.emailed_at && <p className="text-emerald-700">Emailed {new Date(i.emailed_at).toLocaleDateString()} to {(i.emailed_to || []).join(', ')}</p>}
                    {i.alert_to && (i.alert_ok
                      ? <p className="text-[#0077B6]" data-testid={`queue-alert-${i.folder_id}`}>Officer alerted {new Date(i.alert_sent_at).toLocaleString()} ({i.alert_to})</p>
                      : <p className="text-red-600" data-testid={`queue-alert-${i.folder_id}`}>Officer alert to {i.alert_to} failed</p>)}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600">{new Date(i.package_created_at).toLocaleString()}</td>
                  <td className="px-3 py-2 text-xs">
                    {i.review_id ? (
                      <div className="space-y-0.5">
                        <OutcomeBadge outcome={i.review_outcome} />
                        <p className="text-gray-400">{i.reviewed_by} • rev {i.review_revision}</p>
                      </div>
                    ) : <span className="text-gray-400">Not reviewed</span>}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="outline" size="sm" asChild>
                        <a href={`${apiUrl}/api/client-files/download/${i.package_file_id}`} onClick={openAuthed} target="_blank" rel="noopener noreferrer" data-testid={`queue-download-${i.folder_id}`}>
                          <Download className="h-4 w-4 mr-1" />Package
                        </a>
                      </Button>
                      {i.review_id ? (
                        <Button size="sm" className="bg-[#0077B6]" onClick={() => onEditReview(i)} data-testid={`queue-edit-review-${i.folder_id}`}>
                          <Pencil className="h-4 w-4 mr-1" />{i.needs_re_review ? 'Re-review' : 'Edit Review'}
                        </Button>
                      ) : (
                        <Button size="sm" className="bg-orange-600 hover:bg-orange-700" onClick={() => onStartReview(i)} data-testid={`queue-start-review-${i.folder_id}`}>
                          <FileCheck2 className="h-4 w-4 mr-1" />Start Review
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FinalPackageQueue;
